import { useEffect, useRef, useState } from 'react';

import { BrainIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import styles from './ModalAutoBrain.module.scss';

const QUOTE_INTERVAL_MS = 8000;
const FADE_OUT_MS = 400;

interface CircuitPath {
  d: string;
  opacity: number;
  dashArray: number;
  dashOffset: number;
  duration: string;
  delay?: string;
}

const CIRCUIT_PATHS: CircuitPath[] = [
  {
    d: 'M 40 100 Q 40 40 100 40 Q 160 40 160 100',
    opacity: 0.6,
    dashArray: 200,
    dashOffset: 200,
    duration: '1.8s',
  },
  {
    d: 'M 160 100 Q 160 160 100 160 Q 40 160 40 100',
    opacity: 0.6,
    dashArray: 180,
    dashOffset: 180,
    duration: '2.1s',
    delay: '0.3s',
  },
  {
    d: 'M 70 30 L 70 50 Q 70 70 90 70 L 110 70 Q 130 70 130 50 L 130 30',
    opacity: 0.4,
    dashArray: 160,
    dashOffset: 160,
    duration: '2s',
    delay: '0.7s',
  },
  {
    d: 'M 30 70 L 50 70 Q 60 70 60 80 L 60 120 Q 60 130 50 130 L 30 130',
    opacity: 0.4,
    dashArray: 140,
    dashOffset: 140,
    duration: '1.5s',
    delay: '0.2s',
  },
  {
    d: 'M 170 70 L 150 70 Q 140 70 140 80 L 140 120 Q 140 130 150 130 L 170 130',
    opacity: 0.4,
    dashArray: 200,
    dashOffset: 200,
    duration: '1.8s',
    delay: '0.5s',
  },
  {
    d: 'M 70 170 L 70 150 Q 70 140 80 140 L 120 140 Q 130 140 130 150 L 130 170',
    opacity: 0.4,
    dashArray: 180,
    dashOffset: 180,
    duration: '2.1s',
    delay: '0.8s',
  },
];

const CIRCUIT_NODES: [number, number][] = [
  [40, 100],
  [160, 100],
  [100, 40],
  [100, 160],
  [70, 70],
  [130, 130],
];

const AutoBrainLoading = () => {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const { t } = useTranslation();

  const quotes = [
    t('auto_brain.quote1'),
    t('auto_brain.quote2'),
    t('auto_brain.quote3'),
    t('auto_brain.quote4'),
  ];

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setVisible(false);
      timeoutRef.current = setTimeout(() => {
        setQuoteIndex((prev) => (prev + 1) % quotes.length);
        setVisible(true);
      }, FADE_OUT_MS);
    }, QUOTE_INTERVAL_MS);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [quotes.length]);

  return (
    <div className={cn(styles.loading, 'flex flex-col items-center')}>
      <div className="relative flex h-50 w-50 items-center justify-center will-change-transform">
        <svg className="absolute inset-0 size-full transform-gpu" viewBox="0 0 200 200" fill="none">
          {CIRCUIT_PATHS.map(({ d, opacity, dashArray, dashOffset, duration, delay }) => (
            <path
              key={d}
              d={d}
              stroke="currentColor"
              strokeWidth="1"
              fill="none"
              opacity={opacity}
              strokeDasharray={dashArray}
              strokeDashoffset={dashOffset}
              className="animate-[dash-trace_var(--duration)_ease-in-out_infinite] will-change-[stroke-dashoffset]"
              style={{ '--duration': duration, animationDelay: delay } as React.CSSProperties}
            />
          ))}

          {CIRCUIT_NODES.map(([cx, cy], idx) => (
            <circle
              key={`${cx}-${cy}`}
              cx={cx}
              cy={cy}
              r="2"
              fill="currentColor"
              opacity="0.5"
              className="animate-node-pulse will-change-[r,opacity]"
              style={{ animationDelay: `${idx * 0.4}s` }}
            />
          ))}
        </svg>

        <BrainIcon
          className="relative z-10 size-16 transform-gpu animate-circuit-brain-glow text-primary will-change-[filter]"
          strokeWidth={1.5}
        />
      </div>

      <span
        className={cn(
          'text-sm leading-5 font-normal',
          'min-h-10 px-5 transition-all duration-400',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
        )}
      >
        {quotes[quoteIndex]}
      </span>
    </div>
  );
};

export default AutoBrainLoading;
